import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import {
  Lock,
  ChevronRight,
  ChevronLeft,
  Check,
  SkipForward,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface StageData {
  id: string;
  name: string;
  order: number;
  percentageWeight: string;
  isGate: boolean;
}

interface AgentData {
  agentKey: string;
  name: string;
  description: string | null;
  category: string;
  enabled: boolean;
  confidenceThreshold: number;
  triggerEvents: string[];
}

interface GateRequirement {
  fieldName: string;
  displayName: string;
  requirementType: string;
  deferrableUntilStageId: string | null;
}

interface GateData {
  stageId: string;
  stageName: string;
  requirements: GateRequirement[];
}

interface ProcessMapData {
  stages: StageData[];
  events: string[];
  agents: AgentData[];
  gates: GateData[];
}

interface OpportunitySummary {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: string;
  currency: string;
  currentStageId: string;
}

interface StageTransition {
  fromStageId: string | null;
  toStageId: string;
  actorId: string | null;
  direction: string;
  reason: string | null;
  occurredAt: string | null;
}

interface OpportunityDetail {
  summary: OpportunitySummary;
  stageHistory: StageTransition[];
}

interface AgentLogEntry {
  id: string;
  agentId: string;
  status: string;
  confidence: number | null;
  opportunityId?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatEventName(event: string): string {
  return event
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const COL_WIDTH = 240;
const ROW_HEIGHT = 90;

/* ------------------------------------------------------------------ */
/*  Layout builder                                                     */
/* ------------------------------------------------------------------ */

function buildProcessMap(data: ProcessMapData): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const sortedStages = [...data.stages].sort((a, b) => a.order - b.order);
  const gateMap = new Map(data.gates.map((g) => [g.stageId, g]));

  // ---- Column 1: Entry Events ----
  const entryEvents = ['LEAD_CREATED'];
  let col = 0;
  let entryRow = 0;

  for (const event of entryEvents) {
    const nodeId = `event-${event}`;
    nodes.push({
      id: nodeId,
      type: 'eventNode',
      position: { x: col * COL_WIDTH, y: entryRow * ROW_HEIGHT },
      data: { label: formatEventName(event), event },
    });

    // Find agents triggered by this event
    const triggeredAgents = data.agents.filter((a) =>
      a.triggerEvents.includes(event),
    );
    triggeredAgents.forEach((agent, aIdx) => {
      const agentNodeId = `agent-${agent.agentKey}-entry-${event}`;
      nodes.push({
        id: agentNodeId,
        type: 'agentNode',
        position: {
          x: col * COL_WIDTH - 20,
          y: (entryRow + 1 + aIdx) * ROW_HEIGHT,
        },
        data: { agent },
      });
      edges.push({
        id: `e-${nodeId}-${agentNodeId}`,
        source: nodeId,
        target: agentNodeId,
        style: { stroke: '#3B82F6', strokeDasharray: '6 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3B82F6' },
      });
    });
    entryRow += 1 + triggeredAgents.length;
  }

  // ---- Columns 2-N+1: Pipeline Stages ----
  const stageChangedAgents = data.agents.filter((a) =>
    a.triggerEvents.includes('STAGE_CHANGED'),
  );

  sortedStages.forEach((stage, sIdx) => {
    col = sIdx + 1;
    const stageNodeId = `stage-${stage.id}`;

    nodes.push({
      id: stageNodeId,
      type: 'stageNode',
      position: { x: col * COL_WIDTH, y: 0 },
      data: { stage },
    });

    // Stage-to-stage edges
    if (sIdx > 0) {
      const prevStageId = `stage-${sortedStages[sIdx - 1].id}`;
      const gate = gateMap.get(sortedStages[sIdx - 1].id);

      if (gate) {
        const gateNodeId = `gate-${sortedStages[sIdx - 1].id}`;
        nodes.push({
          id: gateNodeId,
          type: 'gateNode',
          position: {
            x: (col - 1) * COL_WIDTH + COL_WIDTH / 2 + 60,
            y: -8,
          },
          data: { gate },
        });
        edges.push({
          id: `e-${prevStageId}-${gateNodeId}`,
          source: prevStageId,
          target: gateNodeId,
          style: { stroke: '#D97706', strokeDasharray: '4 3' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#D97706' },
        });
        edges.push({
          id: `e-${gateNodeId}-${stageNodeId}`,
          source: gateNodeId,
          target: stageNodeId,
          style: { stroke: '#D97706', strokeDasharray: '4 3' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#D97706' },
        });
      } else {
        edges.push({
          id: `e-${prevStageId}-${stageNodeId}`,
          source: prevStageId,
          target: stageNodeId,
          style: { stroke: '#94A3B8' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#94A3B8' },
        });
      }
    } else {
      // Connect entry event to first stage
      edges.push({
        id: `e-entry-to-first-stage`,
        source: `event-LEAD_CREATED`,
        target: stageNodeId,
        style: { stroke: '#94A3B8' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94A3B8' },
      });
    }

    // Stage-changed agents fan out below stages (between stages)
    if (sIdx < sortedStages.length - 1) {
      stageChangedAgents.forEach((agent, aIdx) => {
        const agentNodeId = `agent-${agent.agentKey}-stage-${stage.id}`;
        nodes.push({
          id: agentNodeId,
          type: 'agentNode',
          position: {
            x: col * COL_WIDTH + COL_WIDTH / 2 - 80,
            y: (1 + aIdx) * ROW_HEIGHT,
          },
          data: { agent },
        });
        edges.push({
          id: `e-${stageNodeId}-${agentNodeId}`,
          source: stageNodeId,
          target: agentNodeId,
          style: { stroke: '#7C3AED', strokeDasharray: '6 3' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#7C3AED' },
        });
      });
    }
  });

  // ---- Exit Events Column ----
  const exitCol = sortedStages.length + 1;
  const exitEvents = [
    { event: 'CLOSED_WON', agents: data.agents.filter((a) => a.triggerEvents.includes('CLOSED_WON')) },
    { event: 'STALE_OPPORTUNITY', agents: data.agents.filter((a) => a.triggerEvents.includes('STALE_OPPORTUNITY')) },
  ];

  // Connect last stage to exit events
  const lastStageId = sortedStages.length > 0
    ? `stage-${sortedStages[sortedStages.length - 1].id}`
    : null;

  let exitRow = 0;
  exitEvents.forEach(({ event, agents }) => {
    const nodeId = `event-${event}`;
    nodes.push({
      id: nodeId,
      type: 'eventNode',
      position: { x: exitCol * COL_WIDTH, y: exitRow * ROW_HEIGHT },
      data: { label: formatEventName(event), event },
    });

    if (lastStageId) {
      edges.push({
        id: `e-${lastStageId}-${nodeId}`,
        source: lastStageId,
        target: nodeId,
        style: { stroke: '#94A3B8', strokeDasharray: '4 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94A3B8' },
      });
    }

    agents.forEach((agent, aIdx) => {
      const agentNodeId = `agent-${agent.agentKey}-exit-${event}`;
      nodes.push({
        id: agentNodeId,
        type: 'agentNode',
        position: {
          x: exitCol * COL_WIDTH - 20,
          y: (exitRow + 1 + aIdx) * ROW_HEIGHT,
        },
        data: { agent },
      });
      edges.push({
        id: `e-${nodeId}-${agentNodeId}`,
        source: nodeId,
        target: agentNodeId,
        style: { stroke: '#3B82F6', strokeDasharray: '6 3' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3B82F6' },
      });
    });

    exitRow += 1 + agents.length;
  });

  return { nodes, edges };
}

/* ------------------------------------------------------------------ */
/*  Opportunity overlay                                                */
/* ------------------------------------------------------------------ */

interface OverlayState {
  visitedStageIds: Set<string>;
  currentStageId: string;
  allStageIds: string[];
  agentStatuses: Map<string, { status: string; confidence: number | null }>;
}

function computeOverlay(
  detail: OpportunityDetail,
  sortedStages: StageData[],
  agentLogs: AgentLogEntry[],
): OverlayState {
  const allStageIds = sortedStages.map((s) => s.id);
  const currentStageId = detail.summary.currentStageId;
  const currentIdx = allStageIds.indexOf(currentStageId);

  const visitedStageIds = new Set<string>();
  // All stages before (and including) current are visited
  for (let i = 0; i <= currentIdx && i < allStageIds.length; i++) {
    visitedStageIds.add(allStageIds[i]);
  }
  // Also add any stages from history
  for (const t of detail.stageHistory) {
    visitedStageIds.add(t.toStageId);
  }

  const agentStatuses = new Map<string, { status: string; confidence: number | null }>();
  for (const log of agentLogs) {
    agentStatuses.set(log.agentId, {
      status: log.status,
      confidence: log.confidence,
    });
  }

  return { visitedStageIds, currentStageId, allStageIds, agentStatuses };
}

/* ------------------------------------------------------------------ */
/*  Custom Node Components                                             */
/* ------------------------------------------------------------------ */

function EventNodeComponent({ data }: NodeProps) {
  const d = data as { label: string; event: string; overlay?: OverlayState };
  return (
    <div
      className="flex items-center rounded-xl px-4 py-3 text-white shadow-md"
      style={{
        background: '#1E40AF',
        minWidth: 180,
        minHeight: 48,
      }}
    >
      <Handle type="target" position={Position.Left} className="!bg-card" />
      <div className="text-sm font-semibold">{d.label}</div>
      <Handle type="source" position={Position.Right} className="!bg-card" />
    </div>
  );
}

function StageNodeComponent({ data }: NodeProps) {
  const d = data as { stage: StageData; overlay?: OverlayState };
  const stage = d.stage;
  const overlay = d.overlay;

  let bgClass = 'bg-card';
  let borderClass = 'border-border';
  let opacityClass = '';
  let pulseClass = '';

  if (overlay) {
    const isCurrentStage = overlay.currentStageId === stage.id;
    const isVisited = overlay.visitedStageIds.has(stage.id);
    const currentIdx = overlay.allStageIds.indexOf(overlay.currentStageId);
    const stageIdx = overlay.allStageIds.indexOf(stage.id);
    const isFuture = stageIdx > currentIdx;

    if (isCurrentStage) {
      borderClass = 'border-blue-500';
      pulseClass = 'ring-2 ring-blue-400 ring-offset-1 animate-pulse';
    } else if (isVisited) {
      bgClass = 'bg-emerald-950/40';
      borderClass = 'border-emerald-400';
    }
    if (isFuture) {
      opacityClass = 'opacity-30';
    }
  }

  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 shadow-sm',
        bgClass,
        borderClass,
        opacityClass,
        pulseClass,
      )}
      style={{ minWidth: 160 }}
    >
      <Handle type="target" position={Position.Left} className="!bg-zinc-400" />
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-semibold">{stage.name}</span>
        {stage.isGate && <Lock className="h-3.5 w-3.5 text-amber-500" />}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        {stage.percentageWeight}% weight
      </div>
      <Handle type="source" position={Position.Right} className="!bg-zinc-400" />
    </div>
  );
}

function AgentNodeComponent({ data }: NodeProps) {
  const d = data as { agent: AgentData; overlay?: OverlayState };
  const agent = d.agent;
  const overlay = d.overlay;

  let statusBadge: React.ReactNode = null;
  if (overlay) {
    const entry = overlay.agentStatuses.get(agent.agentKey);
    if (entry) {
      if (entry.status === 'SUCCESS') {
        statusBadge = (
          <span className="flex items-center gap-0.5 rounded-full bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-200">
            <Check className="h-3 w-3" />
            {entry.confidence != null ? `${entry.confidence}%` : ''}
          </span>
        );
      } else if (entry.status === 'SKIPPED_LOW_CONFIDENCE') {
        statusBadge = (
          <span className="flex items-center gap-0.5 rounded-full bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
            <SkipForward className="h-3 w-3" />
            Skipped
          </span>
        );
      }
    }
  }

  const CATEGORY_COLORS: Record<string, string> = {
    PROSPECTING: 'bg-blue-900/40 text-blue-200',
    SCORING: 'bg-emerald-900/40 text-emerald-200',
    INTELLIGENCE: 'bg-purple-900/40 text-purple-200',
    DRAFTING: 'bg-orange-900/40 text-orange-200',
    RISK: 'bg-red-900/40 text-red-200',
    ONBOARDING: 'bg-teal-900/40 text-teal-200',
    RENEWAL: 'bg-cyan-900/40 text-cyan-200',
    GATEKEEPER: 'bg-amber-900/40 text-amber-200',
  };

  return (
    <div
      className={cn(
        'group relative rounded-lg border-l-4 bg-card px-3 py-2 shadow-sm transition-opacity',
        agent.enabled ? 'border-l-purple-600' : 'border-l-purple-300 border-dashed opacity-50',
      )}
      style={{ minWidth: 180, borderTopWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderTopColor: agent.enabled ? '#e2e8f0' : undefined, borderRightColor: agent.enabled ? '#e2e8f0' : undefined, borderBottomColor: agent.enabled ? '#e2e8f0' : undefined, borderStyle: agent.enabled ? 'solid' : 'dashed' }}
    >
      <Handle type="target" position={Position.Top} className="!bg-purple-500" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold">{agent.name}</span>
        {statusBadge}
      </div>
      <div className="mt-0.5 flex items-center gap-1">
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium',
            CATEGORY_COLORS[agent.category] ?? 'bg-muted text-foreground',
          )}
        >
          {agent.category}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {agent.confidenceThreshold}%
        </span>
      </div>
      {/* Tooltip on hover */}
      {agent.description && (
        <div className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-56 rounded-lg border bg-card p-2 text-xs text-muted-foreground shadow-lg group-hover:block">
          {agent.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-purple-500" />
    </div>
  );
}

function GateNodeComponent({ data }: NodeProps) {
  const d = data as { gate: GateData; overlay?: OverlayState };
  const gate = d.gate;

  return (
    <div className="flex items-center justify-center" style={{ width: 44, height: 44 }}>
      <Handle type="target" position={Position.Left} className="!bg-amber-500" />
      <div
        className="flex h-10 w-10 items-center justify-center rounded-sm border-2 border-amber-500 bg-amber-950/40 text-xs font-bold text-amber-300"
        style={{ transform: 'rotate(45deg)' }}
        title={`${gate.stageName} gate: ${gate.requirements.length} requirement(s)`}
      >
        <span style={{ transform: 'rotate(-45deg)' }}>
          {gate.requirements.length}
        </span>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-amber-500" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export function ProcessMapPage() {
  const [processMap, setProcessMap] = useState<ProcessMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Opportunity overlay state
  const [opportunities, setOpportunities] = useState<OpportunitySummary[]>([]);
  const [selectedOppId, setSelectedOppId] = useState<string | null>(null);
  const [overlayState, setOverlayState] = useState<OverlayState | null>(null);
  const [overlayLoading, setOverlayLoading] = useState(false);

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([] as Edge[]);

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      eventNode: EventNodeComponent,
      stageNode: StageNodeComponent,
      agentNode: AgentNodeComponent,
      gateNode: GateNodeComponent,
    }),
    [],
  );

  // Load process map data
  const loadMap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<ProcessMapData>('/process-map');
      setProcessMap(data);
      const graph = buildProcessMap(data);
      setNodes(graph.nodes);
      setEdges(graph.edges);
    } catch {
      setError('Failed to load process map');
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges]);

  // Load opportunities for selector
  const loadOpportunities = useCallback(async () => {
    try {
      const { data } = await apiClient.get<OpportunitySummary[]>('/opportunities', {
        params: { status: 'OPEN' },
      });
      setOpportunities(data);
    } catch {
      setOpportunities([]);
    }
  }, []);

  useEffect(() => {
    loadMap();
    loadOpportunities();
  }, [loadMap, loadOpportunities]);

  // Apply overlay when nodes/overlay change
  const applyOverlay = useCallback(
    (overlay: OverlayState | null) => {
      setNodes((prevNodes) =>
        prevNodes.map((node) => ({
          ...node,
          data: { ...node.data, overlay: overlay ?? undefined },
        })),
      );
    },
    [setNodes],
  );

  // Load opportunity overlay
  const loadOverlay = useCallback(
    async (oppId: string) => {
      if (!processMap) return;
      setOverlayLoading(true);
      try {
        const { data: detail } = await apiClient.get<OpportunityDetail>(
          `/opportunities/${oppId}`,
        );

        // Try to load agent logs — graceful fallback if endpoint isn't filterable
        let agentLogs: AgentLogEntry[] = [];
        try {
          const { data: logs } = await apiClient.get<AgentLogEntry[]>(
            '/agents/log',
            { params: { opportunityId: oppId } },
          );
          agentLogs = logs;
        } catch {
          // Agent log filtering may not be available; stage highlighting still works
        }

        const sortedStages = [...processMap.stages].sort(
          (a, b) => a.order - b.order,
        );
        const overlay = computeOverlay(detail, sortedStages, agentLogs);
        setOverlayState(overlay);
        applyOverlay(overlay);
      } catch {
        // Failed to load; clear overlay
        setOverlayState(null);
        applyOverlay(null);
      } finally {
        setOverlayLoading(false);
      }
    },
    [processMap, applyOverlay],
  );

  // Handle opportunity selection
  function handleOppSelect(oppId: string) {
    if (!oppId) {
      clearOverlay();
      return;
    }
    setSelectedOppId(oppId);
    loadOverlay(oppId);
  }

  function clearOverlay() {
    setSelectedOppId(null);
    setOverlayState(null);
    applyOverlay(null);
  }

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading process map...</p>
      </div>
    );
  }

  if (error || !processMap) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-muted-foreground">
          {error ?? 'No process map data available'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h1 className="text-2xl font-semibold">Process Map</h1>
          <p className="text-sm text-muted-foreground">
            Read-only visualization of the sales process, agents, and stage gates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            {sidebarOpen ? (
              <>
                <ChevronRight className="mr-1 h-4 w-4" />
                Hide Agents
              </>
            ) : (
              <>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Show Agents
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Opportunity Selector */}
      <div className="flex items-center gap-3 border-b px-1 py-2">
        <label className="text-sm font-medium text-muted-foreground">
          Opportunity Journey:
        </label>
        <select
          value={selectedOppId ?? ''}
          onChange={(e) => handleOppSelect(e.target.value)}
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
          disabled={overlayLoading}
        >
          <option value="">-- Default view --</option>
          {opportunities.map((opp) => (
            <option key={opp.id} value={opp.id}>
              {opp.name} ({opp.currency} {Number(opp.estimatedDealValue).toLocaleString()})
            </option>
          ))}
        </select>
        {selectedOppId && (
          <Button variant="ghost" size="sm" onClick={clearOverlay}>
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        )}
        {overlayLoading && (
          <span className="text-xs text-muted-foreground">Loading...</span>
        )}
      </div>

      {/* Canvas + Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1" style={{ minHeight: 500 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.2}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>

        {/* Right Sidebar — Agent Summary */}
        {sidebarOpen && (
          <div className="w-72 shrink-0 overflow-y-auto border-l bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">Agent Summary</h3>
            <div className="space-y-2">
              {processMap.agents.map((agent) => {
                const overlayEntry = overlayState?.agentStatuses.get(agent.agentKey);
                return (
                  <div
                    key={agent.agentKey}
                    className={cn(
                      'rounded-lg border bg-card p-3',
                      !agent.enabled && 'opacity-50',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">{agent.name}</span>
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          agent.enabled
                            ? 'bg-emerald-900/40 text-emerald-200'
                            : 'bg-muted text-foreground',
                        )}
                      >
                        {agent.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {agent.category} &middot; Threshold: {agent.confidenceThreshold}%
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      Triggers: {agent.triggerEvents.map(formatEventName).join(', ')}
                    </div>
                    {overlayEntry && (
                      <div className="mt-1">
                        <span
                          className={cn(
                            'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                            overlayEntry.status === 'SUCCESS'
                              ? 'bg-emerald-900/40 text-emerald-200'
                              : overlayEntry.status === 'SKIPPED_LOW_CONFIDENCE'
                                ? 'bg-amber-900/40 text-amber-200'
                                : 'bg-muted text-foreground',
                          )}
                        >
                          {overlayEntry.status}
                          {overlayEntry.confidence != null && ` (${overlayEntry.confidence}%)`}
                        </span>
                      </div>
                    )}
                    {agent.description && (
                      <p className="mt-1 text-[10px] text-muted-foreground line-clamp-2">
                        {agent.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Legend Bar */}
      <div className="flex items-center gap-6 border-t px-4 py-2">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ background: '#1E40AF' }} />
          <span className="text-xs text-muted-foreground">Event</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-border bg-card" />
          <span className="text-xs text-muted-foreground">Stage</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border-l-2 border-l-purple-600 bg-card" />
          <span className="text-xs text-muted-foreground">Agent</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="h-3 w-3 border-2 border-amber-500 bg-amber-950/40"
            style={{ transform: 'rotate(45deg)' }}
          />
          <span className="text-xs text-muted-foreground">Gate</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-dashed border-purple-700 bg-card opacity-50" />
          <span className="text-xs text-muted-foreground">Disabled</span>
        </div>
        {overlayState && (
          <>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-emerald-400 bg-emerald-950/40" />
              <span className="text-xs text-muted-foreground">Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 animate-pulse rounded border-2 border-blue-500 bg-card" />
              <span className="text-xs text-muted-foreground">Current</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-border bg-card opacity-30" />
              <span className="text-xs text-muted-foreground">Future</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
